/**
 * Debug Helper for investigating overlay issues
 */
class DebugHelper {
  /**
   * Check for high z-index iframes that might be blocking interactions
   */
  static checkForOverlayIframes() {
    const iframes = document.querySelectorAll('iframe');
    const problematicIframes = [];
    
    iframes.forEach((iframe, index) => {
      const styles = window.getComputedStyle(iframe);
      const zIndex = parseInt(styles.zIndex);
      const position = styles.position;
      
      if (zIndex > 1000000 || zIndex === 2147483647) {
        problematicIframes.push({
          element: iframe,
          index,
          zIndex,
          position,
          src: iframe.src,
          style: iframe.getAttribute('style'),
          dimensions: {
            width: styles.width,
            height: styles.height,
            top: styles.top,
            left: styles.left
          }
        });
      }
    });
    
    return problematicIframes;
  }

  /**
   * Attempt to fix overlay issues by modifying problematic iframes
   */
  static fixOverlayIframes() {
    const problematicIframes = this.checkForOverlayIframes();
    
    problematicIframes.forEach(iframe => {
      // Try to hide or modify the iframe
      if (iframe.element) {
        // First, try to set pointer-events to none
        iframe.element.style.pointerEvents = 'none';
        
        // If it's a fixed position iframe covering the whole screen, try to hide it
        if (iframe.position === 'fixed' && 
            (iframe.dimensions.width === '100%' || iframe.dimensions.width === '100vw') &&
            (iframe.dimensions.height === '100%' || iframe.dimensions.height === '100vh')) {
          
          console.warn('Found potentially problematic overlay iframe:', iframe);
          
          // Try multiple approaches to fix the issue
          iframe.element.style.display = 'none';
          iframe.element.style.visibility = 'hidden';
          iframe.element.style.zIndex = '-1';
          iframe.element.style.opacity = '0';
          
          // If it's empty or has no meaningful content, remove it
          if (!iframe.src || iframe.src === 'about:blank') {
            try {
              iframe.element.remove();
              console.log('Removed problematic empty iframe');
            } catch (e) {
              console.error('Could not remove iframe:', e);
            }
          }
        }
      }
    });
    
    return problematicIframes;
  }

  /**
   * Monitor for dynamically added problematic iframes
   */
  static startMonitoring() {
    // Check immediately
    setTimeout(() => this.fixOverlayIframes(), 1000);
    
    // Monitor for new iframes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeName === 'IFRAME') {
            setTimeout(() => {
              const styles = window.getComputedStyle(node);
              const zIndex = parseInt(styles.zIndex);
              
              if (zIndex > 1000000 || zIndex === 2147483647) {
                console.warn('New problematic iframe detected:', node);
                node.style.pointerEvents = 'none';
                
                if (styles.position === 'fixed' && 
                    (styles.width === '100%' || styles.width === '100vw') &&
                    (styles.height === '100%' || styles.height === '100vh')) {
                  node.style.display = 'none';
                }
              }
            }, 100);
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    return observer;
  }

  /**
   * Create a debug panel to show current iframe status
   */
  static createDebugPanel() {
    const panel = document.createElement('div');
    panel.id = 'debug-panel';
    panel.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 10px;
      border-radius: 5px;
      font-family: monospace;
      font-size: 12px;
      z-index: 999999999;
      max-width: 300px;
      display: none;
    `;
    
    const updatePanel = () => {
      const iframes = this.checkForOverlayIframes();
      panel.innerHTML = `
        <strong>Debug Panel</strong><br>
        Problematic iframes: ${iframes.length}<br>
        ${iframes.map(iframe => `
          Z-index: ${iframe.zIndex}<br>
          Position: ${iframe.position}<br>
          Src: ${iframe.src || 'no src'}<br>
          ---
        `).join('')}
        <button onclick="this.parentElement.style.display='none'" style="margin-top: 10px;">Close</button>
      `;
    };
    
    updatePanel();
    document.body.appendChild(panel);
    
    // Show panel on Ctrl+Shift+D
    document.addEventListener('keydown', (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
        if (panel.style.display === 'block') {
          updatePanel();
        }
      }
    });
    
    return panel;
  }
}

export default DebugHelper;